import { useEffect, useMemo, useRef, useState } from "react";
import { EventLayout } from "@/components/EventLayout";
import { useEvent } from "@/context/EventContext";
import { Gate } from "@/components/FeatureGate";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, Gift, Quote, Mail, Eye, Send, Upload, Trash2 } from "lucide-react";

export default function ThankYou() {
  const { eapi, event, reloadEvent } = useEvent();
  const [guests, setGuests] = useState([]);
  const [draft, setDraft] = useState({ subject: "", message: "" });
  const [preview, setPreview] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [tyImage, setTyImage] = useState(event?.thankyou?.image_url || "");
  const [imgUploading, setImgUploading] = useState(false);
  const imgRef = useRef();

  const registry = useMemo(() => {
    const links = (event?.registry || []).map((r) => r.label).filter(Boolean);
    const items = event?.registry_items || [];
    return [...new Set([...items, ...links])];
  }, [event]);

  const load = async () => { const g = await eapi.get("/guests"); setGuests(g.data); };
  useEffect(() => {
    load();
    eapi.get("/thankyou-draft").then(({ data }) => setDraft(data)).catch(() => {});
    // eslint-disable-next-line
  }, [eapi]);
  useEffect(() => { setTyImage(event?.thankyou?.image_url || ""); }, [event]);

  const attending = useMemo(() => guests.filter((g) => g.rsvp_status === "attending"), [guests]);
  const sent = attending.filter((g) => g.thank_you_sent).length;
  const progress = attending.length ? Math.round((sent / attending.length) * 100) : 0;
  const toSend = attending.filter((g) => g.email && !g.linked_to && !g.thank_you_sent).length;

  const patch = async (g, changes) => {
    const body = { gift: g.gift || "", thank_you_sent: g.thank_you_sent || false, thank_you_note: g.thank_you_note || "", ...changes };
    setGuests((gs) => gs.map((x) => (x.id === g.id ? { ...x, ...body } : x)));
    try { await eapi.put(`/guests/${g.id}/thankyou`, body); }
    catch { toast.error("Could not save"); load(); }
  };

  const refreshPreview = async () => {
    try { const { data } = await eapi.post("/thankyou-draft/preview", { ...draft, base_url: window.location.origin }); setPreview(data.html); } catch { /* ignore */ }
  };
  const saveDraft = async () => {
    setSavingDraft(true);
    try { await eapi.put("/thankyou-draft", draft); await refreshPreview(); toast.success("Draft saved. Preview updated"); }
    catch { toast.error("Could not save draft"); }
    finally { setSavingDraft(false); }
  };
  const sendAll = async () => {
    setSending(true);
    try {
      await eapi.put("/thankyou-draft", draft);
      const { data } = await eapi.post("/thankyou-send", { ...draft, base_url: window.location.origin });
      toast.success(`Thank-you sent to ${data.sent} guest(s)${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Image must be 10 MB or smaller."); if (imgRef.current) imgRef.current.value = ""; return; }
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await eapi.post("/thankyou/image", fd);
      setTyImage(data.image_url);
      await reloadEvent();
      if (preview) await refreshPreview();
      toast.success("Photo added to your thank-you email");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setImgUploading(false); if (imgRef.current) imgRef.current.value = ""; }
  };
  const removeImage = async () => {
    try { await eapi.delete("/thankyou/image"); setTyImage(""); await reloadEvent(); if (preview) await refreshPreview(); }
    catch { /* ignore */ }
  };

  return (
    <EventLayout>
      <Gate featureKey="thankyou">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
        <p className="overline">After the event</p>
        <h1 className="font-serif text-4xl tracking-tight mb-6">Thank-you notes</h1>

        <div className="bg-white border border-[#E8E6E1] rounded-lg p-6 mb-6" data-testid="thankyou-draft-card">
          <div className="flex items-center gap-2 mb-4"><Mail size={15} className="text-[#8C9A86]" /><p className="overline">Draft your thank-you email</p></div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-stone-500 mb-1.5 block">Subject</span>
                <input value={draft.subject} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} data-testid="thankyou-subject"
                  className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-stone-500 mb-1.5 block">Message</span>
                <textarea value={draft.message} onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value }))} rows={7} data-testid="thankyou-message"
                  className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
              </label>
              <div>
                <span className="text-xs font-medium text-stone-500 mb-1.5 block">Photo (optional)</span>
                {tyImage ? (
                  <div className="relative rounded-md overflow-hidden border border-[#E8E6E1]" data-testid="ty-image-preview">
                    <img src={tyImage} alt="Thank-you" className="w-full max-h-56 object-contain bg-stone-50" />
                    <button onClick={removeImage} data-testid="ty-image-remove" className="absolute top-2 right-2 rounded-full bg-white/90 text-[#C67A7A] p-1.5 hover:bg-white transition-colors"><Trash2 size={14} /></button>
                  </div>
                ) : (
                  <button onClick={() => imgRef.current?.click()} disabled={imgUploading} data-testid="ty-image-upload"
                    className="flex items-center gap-2 rounded-md border border-dashed border-[#E8E6E1] text-stone-500 px-4 py-3 text-sm hover:border-[#8C9A86] w-full justify-center transition-colors disabled:opacity-50">
                    <Upload size={15} /> {imgUploading ? "Uploading…" : "Add a photo to your email"}
                  </button>
                )}
                <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={uploadImage} data-testid="ty-image-input" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={saveDraft} disabled={savingDraft} data-testid="thankyou-save-draft"
                  className="flex items-center gap-2 rounded-full border border-[#E8E6E1] text-stone-600 px-5 py-2.5 text-sm hover:bg-stone-50 transition-colors disabled:opacity-50">
                  <Eye size={15} /> {savingDraft ? "Saving…" : "Save & preview"}
                </button>
                <button onClick={sendAll} disabled={sending || toSend === 0} data-testid="thankyou-send-all"
                  className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-5 py-2.5 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
                  <Send size={15} /> {sending ? "Sending…" : `Send to ${toSend} attending`}
                </button>
              </div>
              <p className="text-xs text-stone-400">We email attending guests who have an email and haven't been thanked yet, then mark them as thanked. Guests without email are skipped. A link to your event page is always included.</p>
            </div>
            <div>
              <span className="text-xs font-medium text-stone-500 mb-2 block">Live preview</span>
              {preview
                ? <iframe title="thank-you preview" srcDoc={preview} data-testid="thankyou-preview" className="w-full h-[360px] rounded-lg border border-[#E8E6E1] bg-white" />
                : <div className="w-full h-[360px] rounded-lg border border-dashed border-[#E8E6E1] grid place-items-center text-sm text-stone-400" data-testid="thankyou-preview-empty">Click "Save &amp; preview" to see your email</div>}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="overline flex items-center gap-2"><Gift size={13} /> Thank-yous sent</p>
            <span className="text-sm text-stone-500">{sent} of {attending.length}</span>
          </div>
          <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
            <div className="h-full bg-[#8C9A86] transition-all duration-700" style={{ width: `${progress}%` }} data-testid="thankyou-progress" />
          </div>
        </div>

        {attending.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8E6E1] rounded-lg p-12 text-center text-sm text-stone-400">
            Guests who RSVP "attending" will appear here so you can track thank-you notes.
          </div>
        ) : (
          <div className="space-y-3">
            {attending.map((g) => (
              <div key={g.id} data-testid={`thankyou-row-${g.id}`} className="bg-white border border-[#E8E6E1] rounded-lg p-4 sm:p-5">
                <div className="flex items-start gap-4">
                  <button onClick={() => patch(g, { thank_you_sent: !g.thank_you_sent })} data-testid={`thankyou-toggle-${g.id}`}
                    className={`mt-0.5 w-6 h-6 shrink-0 rounded-full border grid place-items-center transition-colors ${g.thank_you_sent ? "bg-[#8C9A86] border-[#8C9A86] text-white" : "border-stone-300 hover:border-[#8C9A86]"}`} title="Mark thank-you sent">
                    {g.thank_you_sent && <Check size={13} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800">{g.first_name} {g.last_name}</p>
                    {g.message && <p className="text-xs text-stone-500 mt-1 flex items-start gap-1.5 italic"><Quote size={12} className="mt-0.5 text-[#D4C5B3] shrink-0" /> {g.message}</p>}
                    <input defaultValue={g.gift || ""} placeholder="Gift received (e.g. crystal vase)…"
                      onBlur={(e) => e.target.value !== (g.gift || "") && patch(g, { gift: e.target.value })} data-testid={`thankyou-gift-${g.id}`}
                      className="mt-3 w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                    {registry.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {registry.map((r, i) => (
                          <button key={i} onClick={() => patch(g, { gift: r })} data-testid={`thankyou-registry-chip-${g.id}-${i}`}
                            className="text-[11px] px-2.5 py-1 rounded-full border border-[#E8E6E1] text-stone-500 hover:bg-[#8C9A86]/10 hover:text-[#5f6b59] transition-colors">{r}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {g.thank_you_sent && <span className="text-[11px] text-[#8C9A86] whitespace-nowrap">Sent ✓</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </Gate>
    </EventLayout>
  );
}
